/**
 * Inline ingredient placeholder resolver.
 *
 * Cooking steps use {{qty:N}}, {{unit:N}}, {{name:N}} placeholders where N is
 * the 0-based sort_order index into the variant's ingredient list.
 *
 * Edge cases handled:
 * - Out-of-bounds N → renders "[unknown]"
 * - baseServings clamped to minimum 1 (prevents division by zero)
 * - Quantities < 10 → displayed as vulgar fractions (1/4, 1/3, 1/2, 2/3, 3/4)
 * - display_unit null or empty → unit placeholder renders as empty string and
 *   surrounding whitespace is collapsed (no double spaces in output)
 * - {{name:N}} mid-sentence is lowercased; sentence-start is preserved
 * - Number formatting respects locale (de-DE uses comma decimal separator)
 */

import { convertForDisplay, type UnitSystem } from './unit-display';
import { unitLabel } from './unit-label';
import { tightenUnits } from './format-amount';

export interface IngredientForPlaceholder {
  sortOrder: number;
  displayQuantity: number | null;
  displayUnit: string | null; // abbreviation string, e.g. "tbsp", "g"
  name: string; // translated name
}

const VULGAR_FRACTIONS: Array<[number, string]> = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
];

const FRACTION_TOLERANCE = 0.04;

/**
 * Units that can't meaningfully be split into fractions in an ingredient
 * list — pieces, eggs, pinches, sheets of gelatin, packets, cloves, slices,
 * leaves, sprigs, bunches, heads, etc. The DB carries an `is_discrete` flag
 * on the `units` table for the rows that exist there; this TS-side set
 * covers the broader vocabulary the AI may emit as a raw abbreviation on a
 * recipe_ingredient row (sheet, packet, leaf, sprig, head, etc.) before a
 * matching units-table row exists.
 */
const DISCRETE_UNITS: ReadonlySet<string> = new Set([
  'pc',
  'pinch',
  'clove',
  'slice',
  'bunch',
  'head',
  'stalk',
  'sprig',
  'leaf',
  'strip',
  'fillet',
  'bulb',
  'wedge',
  'packet',
  'can',
  'jar',
  'sheet',
  'pod',
  'knob',
]);

export function isDiscreteUnit(abbrev: string | null | undefined): boolean {
  if (!abbrev) return false;
  return DISCRETE_UNITS.has(abbrev);
}

/**
 * Format a quantity for display. Uses vulgar fractions for values < 10,
 * rounds to 2 decimal places otherwise. Respects locale for decimal separator.
 *
 * When `unit` is supplied and identifies a discrete count unit (pinch, piece,
 * egg, sheet, packet, etc.), the scaler rounds to the nearest whole and
 * floors at 1 — you can't meaningfully use less than a single pinch or a
 * fraction of an egg, so a 0.25× scaling of "1 pinch" renders "1 pinch"
 * instead of the nonsensical "⅜ Prise" / "⅜ Stück Ei".
 */
export function formatQuantity(
  value: number,
  locale: string = 'en',
  unit?: string | null,
): string {
  // Guard against NaN / Infinity — `value <= 0` does NOT catch NaN. Without
  // this, a future caller passing parseFloat("abc") would render literal
  // "NaN" via the locale-aware decimal path below.
  if (!Number.isFinite(value)) return '0';
  if (value <= 0) return '0';

  // Discrete-unit carve-out — never emit vulgar fractions of a pinch / egg /
  // sheet / packet. Round to whole; minimum 1 (a recipe can't call for less
  // than one of a discrete unit in any meaningful sense).
  if (isDiscreteUnit(unit)) {
    return Math.max(1, Math.round(value)).toString();
  }

  const whole = Math.floor(value);
  const frac = value - whole;

  // Try vulgar fraction representation for values < 10
  if (value < 10 && frac > FRACTION_TOLERANCE) {
    for (const [fracValue, symbol] of VULGAR_FRACTIONS) {
      if (Math.abs(frac - fracValue) < FRACTION_TOLERANCE) {
        return whole === 0 ? symbol : `${whole}${symbol}`;
      }
    }
  }

  // For whole numbers, just show the integer
  if (Math.abs(frac) < 0.001) {
    return whole.toString();
  }

  // For larger values or unmatched fractions, use locale-aware decimal format
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString(locale === 'de' ? 'de-DE' : 'en-US', {
    maximumFractionDigits: 2,
  });
}

/**
 * Is `pos` the start of a sentence? True when there are no non-whitespace
 * characters before it, OR the nearest previous non-whitespace character is
 * sentence-terminating punctuation (. ! ? : ;).
 */
function isSentenceStart(s: string, pos: number): boolean {
  for (let i = pos - 1; i >= 0; i--) {
    const ch = s[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') continue;
    return ch === '.' || ch === '!' || ch === '?' || ch === ':' || ch === ';';
  }
  return true;
}

function lowercaseFirstChar(s: string): string {
  if (!s) return s;
  return s[0].toLowerCase() + s.slice(1);
}

function uppercaseFirstChar(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

/**
 * Pre-pass: repair mismatched adjacent {{qty:A}} {{unit:B}} {{name:C}} triples
 * by trusting the NAME index — but ONLY rewrite the unit index to match the
 * name when the matching ingredient has a non-null, non-empty displayUnit.
 *
 * Gemini has shipped "{{qty:1}} {{unit:4}} {{name:4}}" in the wild — qty from
 * spaghetti (idx 1), unit + name from black pepper (idx 4) — rendering "160
 * tsp black pepper" when the intent was "2 tsp black pepper". The name is the
 * semantic anchor (Gemini is most likely to match the name to what it's
 * talking about; the positional qty/unit drift more easily). validateRecipeOutput
 * now applies THIS SAME repair at generation time (so freshly generated recipes
 * store consistent placeholders instead of burning an AI re-roll on the drift),
 * and this render-time pre-pass covers any legacy rows written before that.
 *
 * The displayUnit-aware unit rewrite avoids dropping a meaningful original unit
 * (e.g. "tsp" from idx 1) onto a name whose ingredient has no unit (e.g. an
 * unmastered count item).
 */
export function repairPlaceholderIndices(
  text: string,
  ingredients: IngredientForPlaceholder[],
): string {
  const byIndex = new Map<number, IngredientForPlaceholder>();
  for (const ing of ingredients) byIndex.set(ing.sortOrder, ing);

  const hasMeaningfulUnit = (idx: number): boolean => {
    const ing = byIndex.get(idx);
    return ing != null && ing.displayUnit != null && ing.displayUnit !== '';
  };

  return text
    .replace(
      /\{\{qty:(\d+)\}\}(\s+)\{\{unit:(\d+)\}\}(\s+)\{\{name:(\d+)\}\}/g,
      (match, aStr, s1, _bStr, s2, cStr) => {
        if (aStr === cStr && _bStr === cStr) return match;
        const c = parseInt(cStr, 10);
        // Only rewrite the unit index when the name's ingredient has a
        // meaningful unit; otherwise prefer the original unit index.
        const unitIdx = hasMeaningfulUnit(c) ? cStr : _bStr;
        return `{{qty:${cStr}}}${s1}{{unit:${unitIdx}}}${s2}{{name:${cStr}}}`;
      },
    )
    .replace(
      // Adjacent qty+unit without a following name (unusual but possible) —
      // trust unit (no name to anchor to).
      /\{\{qty:(\d+)\}\}(\s+)\{\{unit:(\d+)\}\}(?!\s+\{\{name:)/g,
      (match, a, s, b) => (a === b ? match : `{{qty:${b}}}${s}{{unit:${b}}}`),
    );
}

/**
 * Internal: substitute {{qty:N}}/{{unit:N}}/{{name:N}} tokens into a text
 * that has already been through `repairPlaceholderIndices`. Returns the
 * substituted string with no whitespace post-processing.
 */
/**
 * Parse a placeholder portion quantity. Accepts plain numerics (`"1"`, `"0.5"`),
 * unicode/ASCII fractions (`"1/2"`, `"½"`, `"1 1/2"`), and trims whitespace.
 * Rejects German decimal commas (`"1,5"`) — the source token is locale-neutral;
 * locale formatting happens at render via {@link formatQuantity}. Returns
 * `null` on any parse failure so the resolver falls back to `[unknown]`
 * instead of rendering literal `"NaN"`.
 */
export function parseFractionalQty(raw: string): number | null {
  const s = raw.trim();
  if (s.length === 0) return null;
  // Reject German comma decimals — keeps the wire format unambiguous.
  if (/,/.test(s)) return null;
  // Mixed "1 1/2"
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const num = Number(mixed[2]);
    const den = Number(mixed[3]);
    if (den === 0) return null;
    return Number(mixed[1]) + num / den;
  }
  // Plain fraction "1/2"
  const frac = /^(\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const den = Number(frac[2]);
    if (den === 0) return null;
    return Number(frac[1]) / den;
  }
  // Plain decimal / integer
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * The amount of one ingredient used in a SINGLE step, derived from that step's
 * placeholders: the step-specific portion(s) (`{{portion:N|amt|unit}}`), the
 * full quantity (`{{qty:N}}`), or `null` when the step only names the ingredient.
 * Lets the per-step ingredient strip show "1½ TL Salz" (this step's share)
 * instead of the recipe total.
 */
export function stepIngredientAmount(
  instructionText: string,
  ingredient: IngredientForPlaceholder,
  selectedServings: number,
  baseServings: number,
  locale: 'en' | 'de',
  unitSystem: UnitSystem,
): { qty: string; unit: string } | null {
  const scaleFactor = selectedServings / Math.max(1, baseServings);
  const idx = ingredient.sortOrder;

  // Full-quantity reference — {{qty:N}} means the step uses the whole amount.
  if (new RegExp(`\\{\\{qty:${idx}\\}\\}`).test(instructionText)) {
    const scaled = ingredient.displayQuantity == null ? null : ingredient.displayQuantity * scaleFactor;
    const conv = convertForDisplay(scaled, ingredient.displayUnit, unitSystem);
    if (conv.qty == null) return null;
    return { qty: formatQuantity(conv.qty, locale, conv.unit), unit: unitLabel(conv.unit, locale) };
  }

  // Portion reference(s) — {{portion:N|amt|unit}}. Sum shares carrying the same
  // unit (an ingredient split within one step is uncommon but possible).
  const portionRe = new RegExp(`\\{\\{portion:${idx}\\|([^|}]+)\\|([^|}]+)\\}\\}`, 'g');
  let total = 0;
  let unit: string | null = null;
  for (const m of instructionText.matchAll(portionRe)) {
    const amt = parseFractionalQty(m[1]);
    const u = m[2].trim();
    if (amt == null || amt <= 0) continue;
    if (unit != null && unit !== u) continue;
    unit = u;
    total += amt;
  }
  if (unit != null && total > 0) {
    return { qty: formatQuantity(total * scaleFactor, locale, unit), unit: unitLabel(unit, locale) };
  }

  // Named only ({{name:N}} with no amount) → no step-specific quantity.
  return null;
}

function substitutePlaceholders(
  repaired: string,
  ingredients: IngredientForPlaceholder[],
  selectedServings: number,
  baseServings: number,
  locale: 'en' | 'de',
  unitSystem: UnitSystem,
): string {
  const safeBase = Math.max(1, baseServings);
  const scaleFactor = selectedServings / safeBase;

  const byIndex = new Map<number, IngredientForPlaceholder>();
  for (const ing of ingredients) byIndex.set(ing.sortOrder, ing);

  // Pre-compute the scaled + system-converted (qty, unit) for each ingredient
  // once per call so the qty and unit placeholder branches stay in sync.
  const convertedByIndex = new Map<number, { qty: number | null; unit: string | null }>();
  for (const ing of ingredients) {
    const scaled = ing.displayQuantity == null ? null : ing.displayQuantity * scaleFactor;
    convertedByIndex.set(ing.sortOrder, convertForDisplay(scaled, ing.displayUnit, unitSystem));
  }

  // Pass 1 — portion tokens. Render to a self-contained "${qty} ${label}"
  // string (the ingredient name still comes from a separate {{name:N}} token).
  // Done before the qty/unit/name pass so the existing regex doesn't accidentally
  // match a portion's internals.
  const PORTION_RE = /\{\{portion:(\d+)\|([^|}]+)\|([^|}]+)\}\}/g;
  const withPortions = repaired.replace(
    PORTION_RE,
    (_match, indexStr: string, qtyRaw: string, unitRaw: string) => {
      const index = parseInt(indexStr, 10);
      const ing = byIndex.get(index);
      if (!ing) return '[unknown]';
      const baseQty = parseFractionalQty(qtyRaw);
      if (baseQty == null || baseQty <= 0) return '[unknown]';
      const unit = unitRaw.trim();
      if (unit.length === 0 || unit.length > 12) return '[unknown]';
      const scaled = baseQty * scaleFactor;
      const qty = formatQuantity(scaled, locale, unit);
      const label = unitLabel(unit, locale);
      // Render qty + label (label may be empty for `pc`); skip the join space when empty.
      return [qty, label].filter(Boolean).join(' ');
    },
  );

  const resolved = withPortions.replace(
    /\{\{(qty|unit|name):(\d+)\}\}/g,
    (_match, type, indexStr, offset: number) => {
      const index = parseInt(indexStr, 10);
      const ingredient = byIndex.get(index);
      if (!ingredient) return '[unknown]';
      const conv = convertedByIndex.get(index);

      switch (type) {
        case 'qty': {
          if (conv == null || conv.qty == null) return '';
          return formatQuantity(conv.qty, locale, conv.unit);
        }
        case 'unit': {
          return unitLabel(conv?.unit, locale);
        }
        case 'name': {
          // German nouns are always capitalised, so ingredient names must stay
          // upper-cased regardless of position ("die Zwiebel hinzufügen", not
          // "die zwiebel hinzufügen"). Force-uppercase the first character so
          // translations stored lowercase still render correctly.
          if (locale === 'de') {
            return uppercaseFirstChar(ingredient.name);
          }
          // Lowercase mid-sentence — AI often emits "Spinach" but "Add Spinach"
          // reads as a proper noun. Preserve capitalisation at sentence starts.
          // offset is relative to `withPortions` (post-pass-1; portion
          // tokens have been rendered to their full string). Pass-1 may have
          // changed string length; this is the correct frame for the
          // sentence-start check on this replace call.
          return isSentenceStart(withPortions, offset)
            ? ingredient.name
            : lowercaseFirstChar(ingredient.name);
        }
        default:
          return '[unknown]';
      }
    },
  );

  // Defensive guard against unsupported placeholders the AI may have invented
  // (validateRecipeOutput now rejects these, but rows written before that guard
  // can still carry e.g. {{timeMinutes:5}}). Never leak raw braces to the user:
  // a bare-integer payload is an embedded literal value (render it), anything
  // else is dropped. The surrounding whitespace collapse handles spacing.
  const cleaned = resolved.replace(/\{\{([^{}]*)\}\}/g, (_m, inner: string) => {
    const colon = inner.indexOf(':');
    const payload = colon >= 0 ? inner.slice(colon + 1).trim() : '';
    return /^\d+$/.test(payload) ? payload : '';
  });
  // House style: no space between a number and a mass/volume symbol ("350g").
  return tightenUnits(cleaned);
}

/**
 * Resolve all placeholders in a WHOLE step instruction string and apply
 * line-level whitespace normalisation (incl. trimming leading whitespace per
 * line). Use this for whole-instruction calls (e.g. step-list rendering).
 *
 * For mid-instruction segments (e.g. cook-mode rendering text BETWEEN
 * highlighted ingredient triplets), use {@link resolveSegment} instead — that
 * variant preserves the leading whitespace of each segment, which is
 * load-bearing for sentences like "Stir in {{name:0}} and serve" where the
 * post-triplet segment begins with " and serve".
 *
 * @param text - Raw instruction text with {{qty:N}}, {{unit:N}}, {{name:N}} placeholders
 * @param ingredients - Array of ingredients sorted by sort_order (index = sort_order)
 * @param selectedServings - Current serving count chosen by the user
 * @param baseServings - Base serving count the recipe was written for
 * @param locale - 'en' or 'de' for number formatting
 * @returns Resolved text with placeholders replaced by ingredient data
 *
 * Behaviour notes:
 * - {{unit:N}} with a null display_unit expands to '' and surrounding whitespace
 *   is collapsed so "{{qty:0}} {{unit:0}} {{name:0}}" → "2 flour" (no double space).
 * - {{name:N}} mid-sentence is lowercased (e.g. "Add {{name:0}}" → "Add spinach")
 *   but left capitalised at sentence starts.
 *
 * SECURITY NOTE: The returned string is RAW TEXT, not sanitized HTML.
 * Render it as a React text node ({resolvedText}) only — NEVER via dangerouslySetInnerHTML.
 * Ingredient names and step text from the AI pipeline are not HTML-encoded here.
 */
export function resolveFullInstruction(
  text: string,
  ingredients: IngredientForPlaceholder[],
  selectedServings: number,
  baseServings: number,
  locale: 'en' | 'de' = 'en',
  unitSystem: UnitSystem = 'metric',
): string {
  const repaired = repairPlaceholderIndices(text, ingredients);
  const replaced = substitutePlaceholders(
    repaired,
    ingredients,
    selectedServings,
    baseServings,
    locale,
    unitSystem,
  );

  // Collapse runs of 2+ spaces/tabs down to 1, and strip space that ended up
  // next to a comma / closing punctuation because a placeholder collapsed.
  // Trim leading/trailing horizontal whitespace per line. (Newlines preserved.)
  return replaced
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/^[ \t]+/gm, '')
    .replace(/[ \t]+$/gm, '');
}

/**
 * Resolve placeholders in a MID-INSTRUCTION SEGMENT — i.e. text that may begin
 * or end with whitespace that is load-bearing in the surrounding sentence.
 * Identical to {@link resolveFullInstruction} except the per-line leading and
 * trailing horizontal whitespace trims are skipped, so " and serve" stays
 * " and serve" instead of becoming "and serve".
 *
 * Cook Mode calls this on segments between highlighted ingredient triples.
 * Note: this DOES NOT run the index-repair pre-pass — the caller is expected
 * to have already split on a repaired string (see cook-mode.tsx).
 */
export function resolveSegment(
  text: string,
  ingredients: IngredientForPlaceholder[],
  selectedServings: number,
  baseServings: number,
  locale: 'en' | 'de' = 'en',
  unitSystem: UnitSystem = 'metric',
): string {
  const replaced = substitutePlaceholders(
    text,
    ingredients,
    selectedServings,
    baseServings,
    locale,
    unitSystem,
  );

  // Same internal whitespace cleanup as the full path — but NO line-level
  // leading/trailing trim, so segment-leading whitespace is preserved.
  return replaced
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([,.;:!?])/g, '$1');
}

/**
 * @deprecated Use {@link resolveFullInstruction} for whole-instruction calls
 * or {@link resolveSegment} for mid-instruction segments. Kept as an alias
 * for backwards compatibility — behaves identically to resolveFullInstruction.
 */
export const resolvePlaceholders = resolveFullInstruction;
